import {
  NAV_ITEMS_LIST,
  ROUTE_PATHS,
  ROUTE_SEGMENTS,
  type NavItemSegment,
} from "@/constants/navigation";
import {
  NAV_ITEM_SEGMENT_TO_USER_FEATURE_TYPE,
  type UserProfileCanAccess,
} from "@/constants/userFeatureType";
import type { FeaturesProperties } from "@/types/features";
import {
  PERMISSION_GRANT_KEYS,
  type PermissionGrants,
} from "@/lib/permissions/permissionGrantsModel";
import { getLandingPage } from "@/lib/permissions/getLandingPage";
import {
  PERMISSION_RULES,
  type FeatureFlags,
  type PermissionRule,
} from "@/lib/permissions/permissionRules";

const NAV_ITEM_SEGMENTS = new Set(
  Object.values(ROUTE_SEGMENTS) as readonly NavItemSegment[],
);

const isNavItemSegmentValue = (key: string): key is NavItemSegment =>
  NAV_ITEM_SEGMENTS.has(key as NavItemSegment);

const areFeatureFlagsSatisfied = (
  { flags, requiresAll }: FeatureFlags,
  featuresData: Partial<FeaturesProperties>,
): boolean => {
  if (flags.length === 0) {
    return true;
  }
  if (requiresAll) {
    return flags.every((f) => featuresData[f] === true);
  }
  return flags.some((f) => featuresData[f] === true);
};

const collectNavItemFeatureFlagLists = (
  rules: readonly PermissionRule[],
): { readonly [S in NavItemSegment]: readonly FeatureFlags[] } => {
  const lists: Record<NavItemSegment, FeatureFlags[]> = {
    [ROUTE_SEGMENTS.EXECUTIVE_SUMMARY]: [],
    [ROUTE_SEGMENTS.SPARX_CATALOG]: [],
    [ROUTE_SEGMENTS.CHANNEL_DETAIL]: [],
    [ROUTE_SEGMENTS.SPARXS_EDITOR]: [],
    [ROUTE_SEGMENTS.SVG_ALTERATION]: [],
  };

  for (const rule of rules) {
    for (const key of PERMISSION_GRANT_KEYS) {
      if (rule.patch[key] !== true) {
        continue;
      }
      if (!isNavItemSegmentValue(key)) {
        continue;
      }
      if (rule.featureFlags == null || rule.featureFlags.flags.length === 0) {
        continue;
      }
      lists[key].push(rule.featureFlags);
    }
  }
  return lists;
};

const NAV_ITEM_SEGMENT_FEATURE_FLAG_LISTS: {
  readonly [S in NavItemSegment]: readonly FeatureFlags[];
} = collectNavItemFeatureFlagLists(PERMISSION_RULES);

const isNavItemSegmentFeatureDataSatisfied = (
  segment: NavItemSegment,
  featuresData: Partial<FeaturesProperties>,
): boolean =>
  NAV_ITEM_SEGMENT_FEATURE_FLAG_LISTS[segment].every((ff) =>
    areFeatureFlagsSatisfied(ff, featuresData),
  );

const getFirstPathSegmentLower = (pathname: string): string | null => {
  const normalized = normalizePath(pathname);
  if (normalized === "/") {
    return null;
  }
  const first = normalized.replace(/^\//, "").split("/")[0];
  return first !== undefined && first !== "" ? first.toLowerCase() : null;
};

const NAV_ITEM_SEGMENT_VALUES = Object.values(
  ROUTE_SEGMENTS,
) as readonly NavItemSegment[];

const isNavItemSegment = (value: string): value is NavItemSegment =>
  (NAV_ITEM_SEGMENT_VALUES as readonly string[]).includes(value);

export const isNavSegmentAccessible = (
  segment: NavItemSegment,
  permissionGrants: PermissionGrants,
  featuresData: Partial<FeaturesProperties>,
): boolean => {
  if (permissionGrants[segment] !== true) {
    return false;
  }
  return isNavItemSegmentFeatureDataSatisfied(segment, featuresData);
};

export const normalizePath = (pathname: string): string => {
  if (pathname === "") {
    return "/";
  }
  const withoutTrailing = pathname.replace(/\/+$/, "");
  return withoutTrailing === "" ? "/" : withoutTrailing;
};

export type PathAccessContext =
  | { readonly kind: "root" }
  | { readonly kind: "segment"; readonly segment: NavItemSegment }
  | { readonly kind: "outsideKnownRoutes" };

export const parsePathAccess = (pathname: string): PathAccessContext => {
  const normalized = normalizePath(pathname);
  if (normalized === ROUTE_PATHS.HOME) {
    return { kind: "root" };
  }
  const firstSeg = getFirstPathSegmentLower(pathname);
  if (firstSeg == null || !isNavItemSegment(firstSeg)) {
    return { kind: "outsideKnownRoutes" };
  }
  return { kind: "segment", segment: firstSeg };
};

export const isRootIndexPathAllowed = (
  permissionGrants: PermissionGrants,
): boolean => getLandingPage(permissionGrants).action === "redirect";

export const isPathAllowed = (
  path: PathAccessContext,
  permissionGrants: PermissionGrants,
  featuresData: Partial<FeaturesProperties>,
): boolean => {
  switch (path.kind) {
    case "root":
      return isRootIndexPathAllowed(permissionGrants);
    case "outsideKnownRoutes":
      return true;
    case "segment":
      return isNavSegmentAccessible(
        path.segment,
        permissionGrants,
        featuresData,
      );
  }
};

export const firstAccessibleNavPath = (
  permissionGrants: PermissionGrants,
  featuresData: Partial<FeaturesProperties>,
): string | null => {
  for (const item of NAV_ITEMS_LIST) {
    if (isNavSegmentAccessible(item.segment, permissionGrants, featuresData)) {
      return `/${item.segment}`;
    }
  }
  return null;
};

export const firstAccessibleNavPathFromCanAccess = (
  canAccess: UserProfileCanAccess,
): string | null => {
  for (const item of NAV_ITEMS_LIST) {
    const feature = NAV_ITEM_SEGMENT_TO_USER_FEATURE_TYPE[item.segment];
    if (canAccess[feature]) {
      return `/${item.segment}`;
    }
  }
  return null;
};
